/**
 * AIS vessel density layer — WM renders variable-radius blobs whose size
 * and alpha both ramp with traffic intensity, amber when congestion is
 * detected (DeckGLMap.ts:3587-3608). Sentinel aggregates the raw vessel
 * positions into a 1-degree grid client-side; WM's deltaPct congestion
 * signal needs a traffic baseline that does not exist yet, so cells carry
 * deltaPct 0 and render cyan until a baseline is built (plan-doc note).
 */

import { ScatterplotLayer } from '@deck.gl/layers';
import type { Layer } from '@deck.gl/core';
import type { NormalizedEvent } from '@/sources/base-source';

/** Layer IDs managed by this renderer. */
export const AIS_DENSITY_LAYER_IDS = new Set(['ais-density']);

/** One aggregated density cell. */
export interface DensityCell {
  lng: number;
  lat: number;
  /** Normalized traffic intensity 0..1 (cell count / busiest cell). */
  intensity: number;
  /** Percent change vs baseline; >= 15 renders as congestion amber. */
  deltaPct: number;
}

/** Aggregate vessel positions into 1-degree grid cells with normalized intensity. */
export function aggregateDensity(events: NormalizedEvent[]): DensityCell[] {
  const bins = new Map<string, { lng: number; lat: number; count: number }>();

  for (const e of events) {
    if (!e.location) continue;
    const gx = Math.floor(e.location.lng);
    const gy = Math.floor(e.location.lat);
    const key = `${gx},${gy}`;
    const bin = bins.get(key);
    if (bin) {
      bin.count += 1;
    } else {
      bins.set(key, { lng: gx + 0.5, lat: gy + 0.5, count: 1 });
    }
  }

  if (bins.size === 0) return [];

  let max = 0;
  for (const bin of bins.values()) {
    if (bin.count > max) max = bin.count;
  }

  const cells: DensityCell[] = [];
  for (const bin of bins.values()) {
    cells.push({
      lng: bin.lng,
      lat: bin.lat,
      intensity: bin.count / max,
      deltaPct: 0,
    });
  }
  return cells;
}

/** Create the AIS density layer (accessors verbatim from WM DeckGLMap.ts:3591-3604). */
export function createAisDensityLayers(events: NormalizedEvent[]): Layer[] {
  const cells = aggregateDensity(events);
  if (cells.length === 0) return [];

  return [new ScatterplotLayer<DensityCell>({
    id: 'ais-density-layer',
    data: cells,
    pickable: true,
    filled: true,
    radiusUnits: 'meters' as const,
    getPosition: (d) => [d.lng, d.lat],
    getRadius: (d) => 4000 + d.intensity * 8000,
    getFillColor: (d) => {
      const intensity = Math.min(1, Math.max(0.15, d.intensity));
      const alpha = Math.round(40 + intensity * 160);
      const isCongested = (d.deltaPct || 0) >= 15;
      return isCongested
        ? [255, 183, 3, alpha] as [number, number, number, number]
        : [0, 209, 255, alpha] as [number, number, number, number];
    },
    radiusMinPixels: 4,
    radiusMaxPixels: 12,
    updateTriggers: {
      getRadius: [cells.length],
      getFillColor: [cells.length],
    },
  })];
}
